import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'WaiverPdfDocument' }

import { ref, computed, nextTick } from 'vue'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { sanitizeQuillHtml } from '@/util/quillSanitize'
import { renderSignature } from '@/util/waiverSignature'
import { date as fmtWallDate, time as fmtWallTime, formatGroupDate, formatGroupTime } from '@/plugins/dates'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const record = ref<any>({ waiver: {}, consent_fields: [], custom_field_responses: [] })
const logoSrc = ref('')
const sigCanvas = ref<HTMLCanvasElement | null>(null)
const sigW = ref(240)
const sigH = ref(92)
const generatedAt = ref('')

function getGroup() {
  try {
    return JSON.parse(localStorage.getItem('group') || '{}') || {}
  } catch (e) {
    return {}
  }
}

const waiver = computed(() => record.value.waiver || {})

const groupName = computed(() => getGroup().name || 'Off The Couch')

const signerName = computed(() => {
  const w = waiver.value
  const first = w.display_first_name || w.first_name
  const last = w.display_last_name || w.last_name
  const name = [first, last].filter(Boolean).join(' ').trim()
  return name || w.email || ''
})
const email = computed(() => waiver.value.email || '')
const gameName = computed(() => waiver.value.game_name || waiver.value.game || '')
const isMinor = computed(() => !!Number(waiver.value.is_minor))
const notParticipating = computed(() => !!Number(waiver.value.not_participating))

const eventDateText = computed(() => {
  const w = waiver.value
  if (!w.booking_date) return ''
  const g = getGroup()
  // booking_date / start_time are venue wall-clock — format without tz conversion.
  const d = fmtWallDate(w.booking_date, g.date_format || 'MM/DD/YYYY')
  if (Number(w.is_all_day) === 1) {
    return w.all_day_text ? `${d} · ${w.all_day_text}` : d
  }
  const t = w.start_time ? fmtWallTime(w.start_time, g.time_format) : ''
  return t ? `${d} at ${t}` : d
})

const metaItems = computed(() => [
  { label: 'Signer', value: signerName.value || '—' },
  { label: 'Event', value: gameName.value || 'Walk-in' },
  { label: 'Date', value: eventDateText.value || '—' },
  { label: 'Status', value: notParticipating.value ? 'Not participating' : 'Participating' },
  { label: 'Minor', value: isMinor.value ? 'Yes' : 'No' },
  { label: 'Email', value: email.value || '—' },
])

const signedText = computed(() => {
  const w = waiver.value
  const ts = w.created_time
  if (!ts) return ''
  // created_time is a UTC timestamp — group-tz aware formatting.
  const d = formatGroupDate(ts)
  const t = formatGroupTime(ts)
  const who = isMinor.value ? 'guardian' : signerName.value
  const stamp = t ? `${d} at ${t}` : d
  return who ? `Signed by ${who} on ${stamp}` : `Signed on ${stamp}`
})

const waiverHtml = computed(() => {
  const text = waiver.value.waiver_text
  return text ? sanitizeQuillHtml(text) : ''
})

const signatureSrc = computed(() => {
  const sig = waiver.value.signature
  return sig ? renderSignature(sig) : ''
})
const signatureAlt = computed(() =>
  isMinor.value ? `Guardian signature${signerName.value ? ' for ' + signerName.value : ''}` : `Signature${signerName.value ? ' of ' + signerName.value : ''}`
)

// Consent fields: the single-GET returns the consent fields active at sign time
// ({ name, label, is_optin }); the waiver row carries the actual opt-out answers.
const consentRows = computed(() => {
  const w = waiver.value
  const fields = record.value.consent_fields || []
  return fields.map((f: any) => {
    let optedOut = false
    if (f.name === 'Email Opt Out') optedOut = !!Number(w.email_optout)
    else if (f.name === 'Photo Opt Out') optedOut = !!Number(w.picture_optout)
    return { label: f.label || f.name, value: optedOut ? 'Opted out' : 'Opted in', optedOut }
  })
})

const customRows = computed(() =>
  (record.value.custom_field_responses || []).map((r: any) => ({
    label: r.label || '',
    response: r.response != null ? String(r.response) : '',
  }))
)

const filename = computed(() => {
  const name = (signerName.value || 'waiver').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  const id = waiver.value.id ? `-${waiver.value.id}` : ''
  return `waiver-${name}${id}`
})

// Convert a (possibly cross-origin) image URL to a data URL so html2canvas never
// taints the canvas. Returns '' on any failure — the PDF simply omits the logo.
function safeImageToDataUrl(url: string): Promise<string> {
  return new Promise((resolve) => {
    if (!url) return resolve('')
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve('')
        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      } catch (e) {
        resolve('')
      }
    }
    img.onerror = () => resolve('')
    img.src = url
  })
}

// Draw the signature onto the on-page <canvas>. html2canvas reads canvas element pixels
// directly (it does NOT go through the image-loading/data-URL path that left a raw <img>
// blank), so this renders reliably. Buffer is the signature's natural resolution; the
// element is CSS-scaled into a 280×92 box. White fill = no transparency for the export.
function drawSignatureToCanvas(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const canvas = (sigCanvas.value || document.getElementById('waiver-pdf-signature-canvas')) as HTMLCanvasElement | null
    if (!src || !canvas) return resolve(false)
    const img = new Image()
    img.onload = () => {
      try {
        const natW = img.naturalWidth || 600
        const natH = img.naturalHeight || 200
        canvas.width = natW
        canvas.height = natH
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve(false)
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, natW, natH)
        ctx.drawImage(img, 0, 0, natW, natH)
        let dh = 92
        let dw = natH ? (dh * natW) / natH : 240
        if (dw > 280) {
          dw = 280
          dh = natW ? (dw * natH) / natW : 92
        }
        sigW.value = Math.round(dw)
        sigH.value = Math.round(dh)
        resolve(true)
      } catch (e) {
        resolve(false)
      }
    }
    img.onerror = () => resolve(false)
    img.src = src
  })
}

// Per-page PDF margins (inches). A4 paper is a fixed format; content height is not.
const PDF_MARGIN = { top: 0.55, left: 0.6, bottom: 0.75, right: 0.6 }
const A4 = { w: 8.2677, h: 11.6929 }

// Atomic blocks for pagination, in document order. Each top-level section stays whole,
// except the agreement body, which breaks between its paragraphs (so long legal text can
// span pages without keeping the whole block together or splitting mid-line).
function collectAtomicBlocks(root: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = []
  for (const child of Array.from(root.children) as HTMLElement[]) {
    const agreement = child.querySelector('.wpdf-agreement') as HTMLElement | null
    if (agreement) {
      for (const sub of Array.from(child.children) as HTMLElement[]) {
        if (sub === agreement) {
          const paras = Array.from(agreement.children) as HTMLElement[]
          if (paras.length) out.push(...paras)
          else out.push(agreement)
        } else {
          out.push(sub) // section eyebrow
        }
      }
    } else {
      out.push(child) // header, title, metadata, consent, custom, signature section
    }
  }
  return out
}

// Render by COMPOSITING, not coordinate-mapping. html2canvas captures the whole document
// into ONE continuous canvas; we paint the signature onto that canvas at the box's exact
// pixels (measured from the same DOM that produced it, so it physically lands in the box —
// impossible to misalign), then slice the canvas into pages at block boundaries (see
// collectAtomicBlocks) so nothing splits mid-line. Page count derives from the rendered
// canvas — nothing is a fixed height.
async function renderPdf() {
  const root = document.getElementById('waiver-pdf-root')
  if (!root) return
  const SCALE = 2
  const big = await html2canvas(root, { scale: SCALE, useCORS: true, backgroundColor: '#ffffff', logging: false })

  const rootRect = root.getBoundingClientRect()
  const s = big.width / rootRect.width

  // Composite the signature onto the continuous canvas at the box's exact pixels.
  const cv = (sigCanvas.value || document.getElementById('waiver-pdf-signature-canvas')) as HTMLCanvasElement | null
  if (cv) {
    const cRect = cv.getBoundingClientRect()
    const ctx = big.getContext('2d')
    if (ctx) {
      // html2canvas leaves a scale() transform on the returned canvas's context, which
      // would double our destination coords. Reset to identity so we draw at raw pixels.
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.drawImage(cv,
        (cRect.left - rootRect.left) * s, (cRect.top - rootRect.top) * s,
        cRect.width * s, cRect.height * s)
    }
  }

  // Per-page content area, in canvas px.
  const contentW = A4.w - PDF_MARGIN.left - PDF_MARGIN.right
  const pxPerIn = big.width / contentW
  const fullPagePx = (A4.h - PDF_MARGIN.top - PDF_MARGIN.bottom) * pxPerIn

  // Atomic blocks (canvas px) in document order: each section stays whole; the agreement
  // breaks between paragraphs. Page breaks land between blocks so nothing splits mid-line.
  const blocks = collectAtomicBlocks(root).map((el) => {
    const r = el.getBoundingClientRect()
    return { top: (r.top - rootRect.top) * s, bottom: (r.bottom - rootRect.top) * s }
  }).filter((b) => b.bottom > b.top)

  // Decide page slices first (so the footer can read "Page x of N").
  const slices: Array<[number, number]> = []
  let y = 0
  while (y < big.height - 1) {
    let end = Math.min(y + fullPagePx, big.height)
    // If a block straddles the page boundary, end the page at its top so it moves whole to
    // the next page. Blocks are in document order, so the first straddler wins.
    for (const b of blocks) {
      if (b.top > y + 1 && b.top < end - 1 && b.bottom > end + 1) { end = b.top; break }
    }
    if (end <= y) end = Math.min(y + fullPagePx, big.height) // block taller than a page: force-split
    slices.push([y, end])
    y = end
  }
  if (slices.length === 0) slices.push([0, big.height])

  const brand = groupName.value
  const pdf = new (jsPDF as any)('p', 'in', 'a4')
  for (let page = 0; page < slices.length; page++) {
    const [sy, ey] = slices[page]
    const sh = ey - sy
    const pageCanvas = document.createElement('canvas')
    pageCanvas.width = big.width
    pageCanvas.height = Math.max(1, Math.round(sh))
    const pctx = pageCanvas.getContext('2d')
    if (pctx) {
      pctx.fillStyle = '#ffffff'
      pctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
      pctx.drawImage(big, 0, sy, big.width, sh, 0, 0, big.width, sh)
    }
    if (page > 0) pdf.addPage()
    pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', PDF_MARGIN.left, PDF_MARGIN.top, contentW, sh / pxPerIn)
    pdf.setFontSize(8)
    pdf.setTextColor(168, 170, 178)
    pdf.text(brand, 0.5, A4.h - 0.3)
    pdf.text(`Page ${page + 1} of ${slices.length}`, A4.w - 1.4, A4.h - 0.3)
  }
  pdf.save(`${filename.value}.pdf`)
}

/**
 * Generate and download a branded waiver PDF. Returns a promise that resolves once the
 * file has been saved (or generation failed) — use it to drive a loading state.
 * @param data { waiver, consent_fields, custom_field_responses } — typically the object
 *   returned by fetchWaiverDetail(id, { includeSignature: true }).
 */
function generate(data: any): Promise<void> {
  record.value = {
    waiver: (data && data.waiver) || {},
    consent_fields: (data && data.consent_fields) || [],
    custom_field_responses: (data && data.custom_field_responses) || [],
  }
  // Match the group's date format so "Generated" reads consistently with the event date.
  const now = new Date()
  const isoToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  generatedAt.value = fmtWallDate(isoToday, getGroup().date_format || 'MM/DD/YYYY')

  return new Promise<void>((resolve) => {
    ;(async () => {
      try {
        await nextTick()
        const [logo] = await Promise.all([
          safeImageToDataUrl(getGroup().img || ''),
          drawSignatureToCanvas(signatureSrc.value),
        ])
        logoSrc.value = logo
        // Tick + small delay so the logo and layout settle before rasterizing.
        await nextTick()
        await new Promise((r) => setTimeout(r, 60))
        await renderPdf()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Waiver PDF generation error:', e)
      } finally {
        resolve()
      }
    })()
  })
}

expose({ generate })

return { __sfc: true,record, logoSrc, sigCanvas, sigW, sigH, generatedAt, getGroup, waiver, groupName, signerName, email, gameName, isMinor, notParticipating, eventDateText, metaItems, signedText, waiverHtml, signatureSrc, signatureAlt, consentRows, customRows, filename, safeImageToDataUrl, drawSignatureToCanvas, PDF_MARGIN, A4, collectAtomicBlocks, renderPdf, generate }
}

})